import {
    setCookie,
    maxCookieExpirationMins,
    getCookie,
} from '@calm/web-common/dist/cookies';
import isSSR from '@calm/web-common/dist/isSSR';
import { CookieResponse } from '@rhg/components/cookie-dialog';
import useAppConfig from '@rhg/hooks/use-app-config';
import { useDeviceConsent } from '@rhg/hooks/use-device-consent';
import {
    LOCATION_OVERRIDE_COOKIE_NAME,
    OPT_IN_COOKIE_NAME,
} from '@rhg/utils/constants';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

export function getCookieResponseFromCookie() {
    if (isSSR()) {
        return;
    }
    return getCookie(OPT_IN_COOKIE_NAME) as CookieResponse;
}

export interface CookieResponseState {
    isFetched: boolean;
    fetchIfNeeded: () => Promise<void>;
    response?: CookieResponse;
    isResponseRequired: boolean;
    isResponseRequested: boolean;
    deviceCountry?: string;
    isGdprRequired?: boolean;
    setResponse: (cookieResponse: CookieResponse) => void;
    requestResponse: () => void;
}
export const CookieResponseContext = createContext<CookieResponseState>(null!);

export const CookieResponseProvider = ({ children }: PropsWithChildren) => {
    const isInitialized = useRef(false);
    const { NEXT_PUBLIC_ANALYTICS_OPT_IN_COUNTRIES } = useAppConfig();
    const { deviceConsent, isFetched, refetch } = useDeviceConsent();
    const optInResponseFromCookie = useMemo(() => {
        if (isSSR()) {
            return undefined;
        }
        const cookieResponse = getCookieResponseFromCookie();
        return !cookieResponse ? undefined : cookieResponse;
    }, []);

    const deviceCountry = useMemo(
        () =>
            isSSR()
                ? ''
                : getCookie(LOCATION_OVERRIDE_COOKIE_NAME) ||
                  deviceConsent?.ip_country ||
                  '',

        [deviceConsent?.ip_country]
    );

    const isResponseRequired = useMemo(
        () =>
            !isSSR() &&
            NEXT_PUBLIC_ANALYTICS_OPT_IN_COUNTRIES.includes(deviceCountry),
        [NEXT_PUBLIC_ANALYTICS_OPT_IN_COUNTRIES, deviceCountry]
    );

    const [isResponseRequested, setIsResponseRequested] = useState(false);
    const [response, setResponseState] = useState<CookieResponse>();

    useEffect(() => {
        if (!isInitialized.current && isFetched) {
            setIsResponseRequested(
                isResponseRequired && !optInResponseFromCookie
            );
            setResponseState(optInResponseFromCookie);

            isInitialized.current = true;
        }
    }, [isFetched, isResponseRequired, optInResponseFromCookie]);

    const setResponse = useCallback((cookieResponse: CookieResponse) => {
        setCookie(OPT_IN_COOKIE_NAME, cookieResponse, maxCookieExpirationMins);
        setResponseState(cookieResponse);
        setIsResponseRequested(false);
    }, []);

    const requestResponse = () => {
        setIsResponseRequested(true);
    };

    const fetchIfNeeded = useCallback(async () => {
        if (isFetched) {
            return;
        } else {
            await refetch();
        }
    }, [isFetched, refetch]);

    return (
        <>
            <CookieResponseContext.Provider
                value={{
                    isFetched,
                    response,
                    fetchIfNeeded,
                    deviceCountry,
                    isGdprRequired: deviceConsent?.requires_gdpr,
                    isResponseRequired,
                    isResponseRequested,
                    requestResponse,
                    setResponse,
                }}
            >
                {children}
            </CookieResponseContext.Provider>
        </>
    );
};
